import type { GisSite, MandalAggregate, SiteStatus } from '../api/types'

/** Integer share of a total, null-safe (0 when the total is 0 — never NaN/`—%`). */
export function statusShare(n: number, total: number): number {
  return total > 0 ? Math.round((n / total) * 100) : 0
}

/**
 * Markers to draw on the Coverage tab: all sites, or only those matching the Explore Coverage status
 * facet (multi-select union) when one or more is active. The Coverage twin of `filterSitesWithIssues`
 * (Issues tab) — the source is filtered (not dimmed) so marker clusters recompute truthfully from the
 * visible set. An empty `statusFilter` (or off the Coverage tab) means "all".
 */
export function filterSitesByStatus(
  sites: GisSite[],
  statusFilter: readonly SiteStatus[],
  coverageFocus: boolean,
): GisSite[] {
  if (!coverageFocus || statusFilter.length === 0) {
    return sites
  }
  return sites.filter((s) => statusFilter.includes(s.status))
}

/**
 * The four survey-status steps in lifecycle order — shared by the funnel bar, legend, status cards,
 * and the mandal status-mix ranking bars. `bucket` indexes a `SurveyFunnel`; `field` indexes a
 * `MandalAggregate` count; `key` indexes the shared `SITE_STATUS_META` colour.
 */
export const FUNNEL_STEPS: {
  key: SiteStatus
  bucket: 'surveyed' | 'inProgress' | 'overdue' | 'never'
  field: 'surveyedSites' | 'inProgressSites' | 'overdueSites' | 'neverSurveyedSites'
  label: string
}[] = [
  { key: 'surveyed', bucket: 'surveyed', field: 'surveyedSites', label: 'Surveyed' },
  { key: 'in_progress', bucket: 'inProgress', field: 'inProgressSites', label: 'In progress' },
  { key: 'overdue', bucket: 'overdue', field: 'overdueSites', label: 'Overdue' },
  { key: 'never_surveyed', bucket: 'never', field: 'neverSurveyedSites', label: 'Never surveyed' },
]

/** District survey funnel — the four survey-status buckets summed across mandals, with derived %s. */
export interface SurveyFunnel {
  surveyed: number
  inProgress: number
  overdue: number
  never: number
  total: number
  /** surveyed / total — reconciles with the coverage headline. */
  coveragePct: number
  /** The "biggest leak": sites scheduled but not surveyed = overdue + never. */
  leak: number
}

/**
 * Roll the per-mandal survey-status counts up into the district funnel (Coverage lens). The total is
 * the sum of the four mapped-site buckets, so the four shares always sum to 100 and `surveyed/total`
 * equals the coverage headline.
 */
export function surveyFunnel(mandals: MandalAggregate[]): SurveyFunnel {
  let surveyed = 0
  let inProgress = 0
  let overdue = 0
  let never = 0
  for (const m of mandals) {
    surveyed += m.surveyedSites
    inProgress += m.inProgressSites
    overdue += m.overdueSites
    never += m.neverSurveyedSites
  }
  const total = surveyed + inProgress + overdue + never
  return { surveyed, inProgress, overdue, never, total, coveragePct: statusShare(surveyed, total), leak: overdue + never }
}
